import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { PageLoaderFull } from "@jugl-web/ui-components";
import { FullPageError } from "@web-src/modules/common/components/FullPageError";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";

type Status = "pending" | "in-progress" | "error" | "ready";

export const AnonymousSessionGateway: FC<PropsWithChildren> = ({
  children,
}) => {
  const { createSession, isInitialized } = useAnonymousSession();
  const createSessionRef = useRef(createSession);
  createSessionRef.current = createSession;
  const [status, setStatus] = useState<Status>("pending");
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const startSession = useCallback(async () => {
    setStatus("in-progress");
    const success = await createSessionRef.current();
    setStatus(success ? "ready" : "error");
  }, []);

  useEffect(() => {
    if (statusRef.current === "ready") {
      return undefined;
    }

    // Timeout is used to prevent a new session from being created while the user is logging out
    const timer = setTimeout(() => {
      startSession();
    }, 100);

    return () => clearTimeout(timer);
  }, [startSession]);

  if (isInitialized) {
    return <>{children}</>;
  }

  if (status === "error") {
    return <FullPageError onRetry={startSession} />;
  }

  return <PageLoaderFull />;
};
